import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database, Json } from "@/integrations/supabase/types";
import type {
  ClientOfferTextOverrides,
  MaterialsOwnership,
  OfferRow,
  VatMode,
  WizardInput,
} from "@/types/mvpEstimate";
import type { OfferTemplateId } from "@/types/offerTemplates";
import type { TemplateType } from "@/components/offer/offerDocumentTypes";

export interface OfferSharePayload {
  schemaVersion: 1;
  createdAt: string;
  wizard: WizardInput;
  lines: OfferRow[];
  vatMode: VatMode;
  draftLabel: string | null;
  /** Szablon wizualny oferty dla klienta; brak w starych zapisach — fallback po stronie UI. */
  templateId?: OfferTemplateId;
  /** Typ dokumentu — porządek sekcji i wariant treści. */
  templateType?: TemplateType;
  offerTexts?: ClientOfferTextOverrides;
  /** Założenia oferty (lista punktów) — opcjonalnie w udostępnionej ofercie. */
  offerAssumptions?: string[];
  materialsOwnership?: MaterialsOwnership;
  materialsNotes?: string;
  warrantyType?: string;
  paymentTerms?: string;
  exclusions?: string[];
  /** @deprecated Legacy */
  warrantyNotes?: string;
  clientOffer: {
    offerDate: string;
    draftId?: string;
    offerNumber?: string;
    clientName?: string;
    clientAddress?: string;
    clientPhone?: string;
    clientEmail?: string;
    companyName?: string;
    companyNip?: string;
    companyAddress?: string;
    companyPhone?: string;
    companyEmail?: string;
    companyLogoPath?: string;
    companyLogoUrl?: string;
  };
  /** Użytkownik miał dostęp PRO w momencie publikacji — ukrywa branding na stronie publicznej. */
  isPro?: boolean;
}

export type CreateOfferShareResult = { publicToken: string } | { error: string };

/**
 * Base64url — jeden segment ścieżki `/oferta/:token` (bez `/`, `+`, `=`).
 * DB trzyma oryginalny `public_token`; w linkach używamy tej formy.
 */
export function toUrlSafePublicToken(dbToken: string): string {
  return dbToken.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

/** Warianty do wyszukania w DB po tokenie z URL (url-safe lub legacy). */
export function publicTokenLookupVariants(urlToken: string): string[] {
  const trimmed = urlToken.trim();
  if (!trimmed) return [];

  const variants = new Set<string>();
  // Link /oferta/:token — padding usunięty; DB (base64url) może mieć "=" na końcu.
  for (let pad = 0; pad <= 3; pad += 1) {
    variants.add(`${trimmed}${"=".repeat(pad)}`);
  }
  // Legacy: w DB "+"/"/", w URL "-"/"_".
  const restored = trimmed.replace(/-/g, "+").replace(/_/g, "/");
  if (restored !== trimmed) {
    for (let pad = 0; pad <= 3; pad += 1) {
      variants.add(`${restored}${"=".repeat(pad)}`);
    }
  }
  return [...variants];
}

/** Param z routera — dekoduje %2F itd. (stare linki); nowe linki są już base64url. */
export function decodePublicTokenRouteParam(routeParam: string | undefined): string {
  if (!routeParam) return "";
  try {
    return decodeURIComponent(routeParam).trim();
  } catch {
    return routeParam.trim();
  }
}

/** URL do Edge Function `get-offer-share` (`public_token` w query = token z linku, url-safe). */
export function buildGetOfferShareFunctionUrl(supabaseBaseUrl: string, publicToken: string): string {
  const base = supabaseBaseUrl.replace(/\/$/, "");
  return `${base}/functions/v1/get-offer-share?public_token=${encodeURIComponent(publicToken.trim())}`;
}

export async function createOfferShare(
  supabase: SupabaseClient<Database>,
  userId: string,
  draftId: string | null,
  payload: OfferSharePayload
): Promise<CreateOfferShareResult> {
  let existingQuery = supabase
    .from("offer_shares")
    .select("id, public_token")
    .eq("owner_user_id", userId)
    .eq("status", "active")
    .limit(1);
  existingQuery =
    draftId === null ? existingQuery.is("draft_id", null) : existingQuery.eq("draft_id", draftId);

  const { data: existingRow, error: existingError } = await existingQuery.maybeSingle();
  if (existingError) {
    console.error("[createOfferShare] existing share lookup failed:", existingError);
    return { error: existingError.message };
  }

  if (existingRow?.id && existingRow.public_token) {
    const { error: updateError } = await supabase
      .from("offer_shares")
      .update({ payload_json: payload as unknown as Json })
      .eq("id", existingRow.id)
      .eq("owner_user_id", userId);

    if (updateError) {
      console.error("[createOfferShare] Supabase update failed:", updateError);
      return { error: updateError.message };
    }
    return { publicToken: toUrlSafePublicToken(existingRow.public_token) };
  }

  const { data, error } = await supabase
    .from("offer_shares")
    .insert({
      owner_user_id: userId,
      draft_id: draftId,
      payload_json: payload as unknown as Json,
    })
    .select("public_token")
    .single();

  if (error) {
    console.error("[createOfferShare] Supabase insert failed:", error);
    return { error: error.message };
  }
  const publicToken = data?.public_token;
  if (!publicToken) {
    return { error: "Brak public_token w odpowiedzi serwera." };
  }
  return { publicToken: toUrlSafePublicToken(publicToken) };
}
